import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button, Tag, Space, Spin, message, Tabs, Empty, Card,
  Statistic, Row, Col, Progress, Drawer
} from 'antd';
import {
  ArrowLeftOutlined, DownloadOutlined, BarChartOutlined,
  TableOutlined, EyeOutlined, ReloadOutlined
} from '@ant-design/icons';
import moment from 'moment';
import SurveyService from '../../../services/SurveyService';
import { PageContainer, PageHeader, StyledTable, PaginationText } from '../styled';

const SurveyResponsesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [activeTab, setActiveTab] = useState('summary');
  const [drawerResponse, setDrawerResponse] = useState(null);

  const loadAnalytics = useCallback(async () => {
    const result = await SurveyService.getAnalytics(id);
    if (result.success) setAnalytics(result.data);
    else message.error(result.error);
  }, [id]);

  const loadResponses = useCallback(async () => {
    const result = await SurveyService.getResponses(id, {
      page: pagination.current,
      limit: pagination.pageSize
    });
    if (result.success) {
      setResponses(result.data.responses || []);
      setPagination((p) => ({ ...p, total: result.data.pagination?.total || 0 }));
    } else {
      message.error(result.error);
    }
  }, [id, pagination.current, pagination.pageSize]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadAnalytics(), loadResponses()]).finally(() => setLoading(false));
  }, [loadAnalytics, loadResponses]);

  const handleExport = async () => {
    try {
      const { data: blob } = await SurveyService.exportResponses(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `survey-${id}-responses.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Export downloaded');
    } catch (err) {
      message.error('Failed to export responses');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>
      </PageContainer>
    );
  }

  const columns = [
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date) => moment(date).format('MMM D, YYYY h:mm A')
    },
    {
      title: 'Respondent',
      key: 'respondent',
      render: (_, r) => {
        const info = r.respondentInfo || {};
        if (info.email || info.name) {
          return (
            <div>
              <div>{info.name || '—'}</div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>{info.email || ''}</div>
            </div>
          );
        }
        return <Tag>Anonymous</Tag>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={status === 'completed' ? 'green' : 'orange'}>{status}</Tag>
      )
    },
    {
      title: 'Time Spent',
      dataIndex: 'timeSpentSeconds',
      key: 'timeSpentSeconds',
      width: 120,
      render: (s) => s ? `${Math.floor(s / 60)}m ${s % 60}s` : '—'
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, r) => (
        <Button type="text" icon={<EyeOutlined />} onClick={() => setDrawerResponse(r)} />
      )
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <Button
            icon={<ArrowLeftOutlined />}
            type="text"
            onClick={() => navigate('/admin/surveys')}
            style={{ marginBottom: 8 }}
          >
            Back to Surveys
          </Button>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { loadAnalytics(); loadResponses(); }}>
            Refresh
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
            Export CSV
          </Button>
        </Space>
      </PageHeader>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Total Completed" value={analytics?.totalResponses || 0} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Avg. Completion Time"
              value={analytics?.avgCompletionSeconds ? `${Math.floor(analytics.avgCompletionSeconds / 60)}m ${analytics.avgCompletionSeconds % 60}s` : '—'}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Questions"
              value={analytics?.questionStats?.length || 0}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'summary',
            label: <span><BarChartOutlined /> Summary</span>,
            children: <SummaryView questionStats={analytics?.questionStats || []} />
          },
          {
            key: 'individual',
            label: <span><TableOutlined /> Individual Responses</span>,
            children: (
              <StyledTable
                columns={columns}
                dataSource={responses}
                rowKey="_id"
                size="middle"
                scroll={{ x: 800 }}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total, range) => (
                    <PaginationText>Showing {range[0]}-{range[1]} of {total} responses</PaginationText>
                  ),
                  onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize }))
                }}
              />
            )
          }
        ]}
      />

      <Drawer
        title="Response Details"
        open={!!drawerResponse}
        onClose={() => setDrawerResponse(null)}
        width={600}
      >
        {drawerResponse && (
          <div>
            <Card size="small" style={{ marginBottom: 16 }}>
              <div><strong>Submitted:</strong> {moment(drawerResponse.createdAt).format('MMM D, YYYY h:mm A')}</div>
              <div><strong>Status:</strong> {drawerResponse.status}</div>
              {drawerResponse.respondentInfo?.email && (
                <div><strong>Email:</strong> {drawerResponse.respondentInfo.email}</div>
              )}
              {drawerResponse.respondentInfo?.name && (
                <div><strong>Name:</strong> {drawerResponse.respondentInfo.name}</div>
              )}
              {drawerResponse.timeSpentSeconds > 0 && (
                <div><strong>Time:</strong> {Math.floor(drawerResponse.timeSpentSeconds / 60)}m {drawerResponse.timeSpentSeconds % 60}s</div>
              )}
            </Card>

            {(drawerResponse.answers || []).map((a, idx) => (
              <Card key={idx} size="small" style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{a.questionText}</div>
                <div style={{ color: '#262626' }}>
                  {Array.isArray(a.answer) ? a.answer.join(', ') : String(a.answer ?? '—')}
                </div>
              </Card>
            ))}
          </div>
        )}
      </Drawer>
    </PageContainer>
  );
};

const SummaryView = ({ questionStats }) => {
  if (!questionStats.length) {
    return <Empty description="No response data yet" />;
  }
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {questionStats.map((q) => (
        <Card key={q.questionId} title={q.question}>
          <div style={{ marginBottom: 12, color: '#8c8c8c' }}>
            {q.responseCount} responses · {q.skipCount} skipped
          </div>
          {q.optionCounts && (
            <div>
              {Object.entries(q.optionCounts).sort((a, b) => b[1] - a[1]).map(([label, count]) => {
                const pct = q.responseCount > 0 ? Math.round((count / q.responseCount) * 100) : 0;
                return (
                  <div key={label} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{label}</span>
                      <span><strong>{count}</strong> ({pct}%)</span>
                    </div>
                    <Progress percent={pct} showInfo={false} strokeColor="#E31B23" />
                  </div>
                );
              })}
            </div>
          )}
          {q.average !== undefined && (
            <div>
              <Statistic title="Average" value={q.average} suffix={`/${q.max}`} />
              <div style={{ marginTop: 8, color: '#8c8c8c' }}>
                Range: {q.min} – {q.max}
              </div>
            </div>
          )}
          {q.sampleAnswers && (
            <div>
              <div style={{ marginBottom: 8, color: '#8c8c8c' }}>Recent answers:</div>
              {q.sampleAnswers.slice(0, 5).map((ans, idx) => (
                <div key={idx} style={{
                  padding: '6px 10px', background: '#fafafa', borderRadius: 4, marginBottom: 4
                }}>
                  {String(ans).slice(0, 200)}
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}
    </Space>
  );
};

export default SurveyResponsesPage;
